import { getAxiosClient } from './common';

const axios = getAxiosClient();

/** Client-facing projection shape returned by GET /v1/extract/projections. */
export interface ExtractProjection {
  id: string;
  label: string;
  epsgCode: string;
  fmeFormat: string;
}

/**
 * Fetch the projection options for the FME extract form, scoped to a project: the backend
 * returns only projections whose coverage area intersects the project (plus any global ones).
 */
export async function getExtractProjections(projectId: string): Promise<ExtractProjection[]> {
  const { data } = await axios.get('/v1/extract/projections', { params: { projectId } });
  return data;
}

/**
 * Fetch the best-fit projection for a project (nearest intersecting coverage zone), or `null`
 * when the project sits inside no projection zone (the caller then keeps its default).
 */
export async function getDefaultExtractProjection(projectId: string): Promise<ExtractProjection | null> {
  const { data } = await axios.get('/v1/extract/projections/default', { params: { projectId } });
  return data;
}

/** Client-facing template shape returned by GET /v1/extract/organizationExtractTemplates. */
export interface ExtractTemplateOption {
  extractTemplateId: string;
  name: string;
}

/** Fetch the extract template options available to an organization. */
export async function getOrganizationExtractTemplates(organizationId: string): Promise<ExtractTemplateOption[]> {
  const { data } = await axios.get('/v1/extract/organizationExtractTemplates', {
    params: { organizationId },
  });
  return data;
}

export interface ProjectExtractRequest {
  projectId: string;
  // Required so canPerformOrgActionMiddleware can authorize the org-level DownloadData action.
  organizationId: string;
  // Raw layer IDs — the backend maps them to FME layer names (don't transform client-side).
  layerIds: string[];
  format: string;
  // Every format requested in one submission. The server unions this with `format` and de-duplicates,
  // so sending both is safe.
  formats?: string[];
  projectionId: string;
  extractTemplateId?: string | null;
  ccEmails?: string[];
}

export interface ProjectExtractResponse {
  success: boolean;
  // Present only when the multi-format export flow handled the request; groups the submission's
  // download records.
  submissionId?: string;
  formats?: string[];
  /** Selected layers the server skipped for having no assets inside the project boundary. */
  droppedLayerIds?: string[];
}

/** Submit a project extract request, kicking off the FME download job. */
export async function submitProjectExtract(body: ProjectExtractRequest): Promise<ProjectExtractResponse> {
  const { data } = await axios.post('/v1/extract/projectExtract', body);
  return data;
}

// ---------------------------------------------------------------------------
// Admin (super-admin) extract tools — backed by /v1/internal/extract/*.
// ---------------------------------------------------------------------------

/** Extract automation row as returned by the admin endpoints. */
export interface ExtractAutomation {
  id: string;
  name: string;
  description: string | null;
  automationId: string;
  webhookUrl: string;
  status: string;
  createdAt: string;
}

/** Extract template row as returned by the admin endpoints. */
export interface ExtractTemplate {
  id: string;
  workspace: string;
  name: string;
  status: string;
  createdAt: string;
}

/** Active template→organization assignment, with display names, for the admin state view. */
export interface ExtractTemplateOrgAssignment {
  id: string;
  extractTemplateId: string;
  templateName: string;
  organizationId: string;
  organizationName: string;
  createdAt: string;
}

export interface ExtractAdminState {
  automations: ExtractAutomation[];
  activeTemplates: ExtractTemplate[];
  templateOrgAssignments: ExtractTemplateOrgAssignment[];
  /** Total number of organizations (all statuses) — used to detect "available to all". */
  organizationCount: number;
}

/** Fetch the extract admin state (every automation, active templates, assignments). */
export async function getExtractAdminState(): Promise<ExtractAdminState> {
  const { data } = await axios.get('/v1/internal/extract/state');
  return data;
}

/** Trigger the projections sync job (refreshes projections from ESRI). */
export async function syncExtractProjections(): Promise<{ success: true }> {
  const { data } = await axios.post('/v1/internal/extract/syncProjections');
  return data;
}

export interface CreateExtractAutomationBody {
  name: string;
  description?: string;
  automationId: string;
  webhookUrl: string;
}

/** Create a new extract automation, archiving all previously-active ones. */
export async function createExtractAutomation(
  body: CreateExtractAutomationBody,
): Promise<{ success: true; automation: ExtractAutomation }> {
  const { data } = await axios.post('/v1/internal/extract/automations', body);
  return data;
}

/** Make an archived extract automation active, archiving the currently-active one. */
export async function activateExtractAutomation(id: string): Promise<{ success: true; automation: ExtractAutomation }> {
  const { data } = await axios.post(`/v1/internal/extract/automations/${id}/activate`);
  return data;
}

/** Permanently delete an archived extract automation; the API refuses the active one with 409. */
export async function deleteExtractAutomation(id: string): Promise<{ success: true }> {
  const { data } = await axios.delete(`/v1/internal/extract/automations/${id}`);
  return data;
}

export interface CreateExtractTemplateBody {
  workspace: string;
  name: string;
}

/** Create a new extract template. */
export async function createExtractTemplate(
  body: CreateExtractTemplateBody,
): Promise<{ success: true; template: ExtractTemplate }> {
  const { data } = await axios.post('/v1/internal/extract/templates', body);
  return data;
}

export interface AssignExtractTemplateBody {
  extractTemplateId: string;
  organizationId?: string;
  allOrgs?: boolean;
}

/** Assign an extract template to one organization or to all active organizations. */
export async function assignExtractTemplate(
  body: AssignExtractTemplateBody,
): Promise<{ success: true; assigned: number; skipped: number }> {
  const { data } = await axios.post('/v1/internal/extract/templates/assign', body);
  return data;
}
